import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import useShortcuts from "src/hooks/useShortcuts"
import { actionForKey, isPlainKeyPress } from "src/utils/shortcuts"
import wsClient from "src/wsClient"
import { timeLeftLabel } from "src/utils/duration"
import {
  AUTO_ADVANCE_RETRY_MS,
  canClaimHost,
  hostDisconnected,
  millisecondsUntilDeadline,
  millisecondsUntilPauseLapses,
  NON_HOST_GRACE_SECONDS,
  noteAutoAdvance,
  pausesLeft,
  secondsUntilHostClaim,
  shouldAdvanceRound,
} from "src/utils/rounds"

import "./RoundHandler.css"

/* `lookingBack`: the player is reading a past round of this game (requirement 35, H4–H5). The
   clock keeps running — every effect below still turns the round for a host — but there is no
   END TURN, no pause and no key for either: the board on screen is not the one an order is for. */
/* `pauseRules`: the server's `MAX_PAUSES_PER_PLAYER` and `MAX_PAUSE_SECONDS` (`rules_config`), for
   the pause button's budget; without them the button is offered as before and the server decides. */
const RoundHandler = ({ ready, userId, game, lookingBack = false, pauseRules = null }) => {
  const { t } = useTranslation()
  const shortcuts = useShortcuts()
  // The retry timer below outlives the render that started it, so it reads the NEWEST game and
  // socket state through a ref. Reading the closure's copy, a game paused after the deadline still
  // looked unpaused to the timer: it sent next_round every retry, and every refusal toasted
  // "the game is paused" once a second.
  const latestRound = useRef({ ready, userId, game })
  latestRound.current = { ready, userId, game }
  const trySendingNextRound = () => {
    const { ready: connected, userId: viewer, game: current } = latestRound.current
    if (!shouldAdvanceRound({ connected, userId: viewer, game: current })) return
    // Send the tick we believe we are on. During a host handover two clients can briefly both
    // think they are host; the server rejects the stale one instead of processing a second round.
    noteAutoAdvance()
    wsClient.send("game", { action: "next_round", expected_tick: current.tick })
  }

  // Ensure the check is made after the deadline, with an offset depending on the ticktime. The
  // host's timer fires at the deadline; every other seat's fires a few seconds later
  // (`NON_HOST_GRACE_SECONDS`), and `shouldAdvanceRound` is what decides whether this seat may
  // turn the round at all — a non-host only turns one that is overdue, or one everybody is
  // ready for while the host is away (the effect below). One rule at every round
  // length (owner, 2026-09-22): a host who is here turns the round; if not, whoever is here does
  // at the second zero and takes the clock, and a player who opens a game that was waiting on
  // nobody finds it overdue and turns it within seconds of arriving.
  useEffect(() => {
    if (!ready) return

    const cleanupFns = []

    // Before this, tickTime being null on round 0 made `new Date(null)` resolve to 1970, so the
    // offset was about -1.8 billion seconds.
    const offset = millisecondsUntilDeadline(game, { userId })

    const offsetTimer = setTimeout(() => {
      trySendingNextRound()
      const intervalId = setInterval(() => {
        trySendingNextRound()
      }, AUTO_ADVANCE_RETRY_MS)

      cleanupFns.push(() => clearInterval(intervalId))
    }, offset)
    cleanupFns.push(() => clearTimeout(offsetTimer))
    return () => {
      cleanupFns.forEach((fn) => fn())
    }
    // game.host.id belongs here: a player promoted mid-game must start ticking, and this
    // effect returns early for non-hosts.
  }, [ready, game.tickTime, userId, game.host.id])

  // A pause lapses by itself (security audit, 2026-10-25). The server has no clock, so at
  // `pausedUntil` the host — any other seat in the fight a few seconds later — says so with
  // `resume_game`, and the server starts the clock as of the second it lapsed. The later seats'
  // tries find it running (`GameNotPaused`), which is read as the answer to a timer and not shown.
  useEffect(() => {
    if (!ready) return undefined
    const wait = millisecondsUntilPauseLapses(game, { userId })
    if (wait === null) return undefined
    const timer = setTimeout(() => {
      const { ready: connected, game: current } = latestRound.current
      if (!connected || !current?.pausedBy) return
      noteAutoAdvance()
      wsClient.send("game", { action: "resume_game" })
    }, wait)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, game.pausedBy, game.pausedUntil, userId, game.host?.id])

  // When the game changes, ensures if we need to move to next round
  // AKA: All the players have clicked ready
  //
  // `ready` (the socket being up) is a dependency too: in a bot-vs-bot game every player is
  // already ready before the spectator-host's socket even connects, and nothing else will ever
  // update `game` to re-trigger this — the first round only turned when the deadline timer got
  // around to it. A player-host never noticed, because their own actions produce game updates.
  //
  // A seat that is not the host holds its try back by NON_HOST_GRACE_SECONDS (owner, 2026-09-29):
  // it only ever turns a ready round when the host is away, and the seconds are what give a host
  // who is reconnecting the round first and keep two seats from racing the tick every time. A
  // second try that loses the race anyway is a StaleRound, which nobody is shown.
  useEffect(() => {
    if (game.host?.id === userId) {
      trySendingNextRound()
      return undefined
    }
    const timer = setTimeout(trySendingNextRound, NON_HOST_GRACE_SECONDS * 1000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, ready])

  // The END TURN control. It used to live only as a per-row "READY" button in PlayerStatuses;
  // this is the same "set_ready" websocket action, now surfaced as the top command bar's primary
  // action so it doesn't require scrolling the player list to find it. Since Q32 it is a toggle:
  // "unset_ready" takes the ready back while the round has not resolved, so pressing end turn no
  // longer locks the player out of the rest of the round.
  const player = game.players.find((p) => p.user.id === userId)
  const isPlayerReady = Boolean(player?.ready)
  const handleEndTurn = () => {
    // Handing the turn over is the player's own act, sounded by `orderSounds` as the order leaves;
    // the round *turning* is a different sound and arrives from the server for everybody at once
    // (see the game slice's socket thunk).
    wsClient.send("game", { action: "set_ready" })
  }
  const handleCancelReady = () => {
    wsClient.send("game", { action: "unset_ready" })
  }

  // The host is the clock. When its socket is down the round cannot turn over early, so once a
  // player is done ("waiting") tell them why nothing happens, and — after the round the host is
  // granted anyway, or at once when everybody is ready — let them turn it themselves. The timer above does it for them a few seconds
  // in; the button is for the player who would rather not wait those seconds. Turning an overdue
  // round while the host is away makes this player the host (the server's rule), so there is no
  // separate claim any more.
  const hostGone = hostDisconnected(game) && game.host.id !== userId
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntilHostClaim(game))
  useEffect(() => {
    if (!hostGone) return undefined
    setSecondsLeft(secondsUntilHostClaim(game))
    const timer = setInterval(() => setSecondsLeft(secondsUntilHostClaim(game)), 1000)
    return () => clearInterval(timer)
  }, [hostGone, game])
  const claimable = hostGone && canClaimHost({ userId, game })
  const handleTurnRound = () => wsClient.send("game", { action: "next_round", expected_tick: game.tick })

  // The keyboard end of the same button (owner, 2026-09-08). It is a toggle exactly as the button
  // is — a key that could only ever say "ready" would lock the player out of the rest of their
  // round, which is the bug Q32 fixed for the button. Bound in the profile pop-up like the cell
  // orders, and read through a ref so the listener presses what the button currently shows.
  //
  // Every modal swallows it, the city view included: unlike a cell order, ending the round is not
  // something the player can still mean while a sheet is open over the map, and the sheets are
  // full of the numbers they are deciding on.
  const latest = useRef(() => {})
  latest.current = () => {
    // While the host is gone and the button has become NEXT ROUND, the key does nothing: turning
    // the round for everybody is not what somebody pressing "end turn" asked for.
    if (claimable && isPlayerReady) return
    if (isPlayerReady) handleCancelReady()
    else handleEndTurn()
  }
  useEffect(() => {
    if (!ready || !player || player.eliminated || lookingBack) return undefined
    const onKeyDown = (event) => {
      if (!isPlainKeyPress(event) || ModalService.hasOpenModals()) return
      if (actionForKey(shortcuts, event.key) !== "endTurn") return
      event.preventDefault()
      latest.current()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [ready, player, shortcuts, lookingBack])

  // Pause: any player stops the clock (the host stops sending next_round — the server refuses it
  // anyway), any player starts it again. Shown to everyone with who pressed it.
  const pausedBy = game.pausedBy
  // The budget is the server's (`NoPausesLeft` past it); the button greys out when it is spent.
  const left = pausesLeft(game, userId, pauseRules?.MAX_PAUSES_PER_PLAYER)
  const noPausesLeft = left === 0
  const handlePause = () => wsClient.send("game", { action: "pause_game" })
  const handleResume = () => wsClient.send("game", { action: "resume_game" })

  // A spectator has no turn to end and no pause to press. The effects above still run, which is
  // how a spectator *host* drives a bot-vs-bot game's clock without a single button: with every
  // player (bot) ready, each broadcast triggers the next round. An eliminated player is the same:
  // no turn to end, but a host who fell with only bots left standing still drives the clock.
  if (!player || player.eliminated || lookingBack) return null

  return (
    <div className="round-controls">
      {pausedBy && (
        <span className="paused-notice" role="status">
          {t("game.topBar.pausedBy", { username: pausedBy.username })}
        </span>
      )}
      {pausedBy ? (
        <button
          type="button"
          className="end-turn-button resume-button"
          onClick={handleResume}
          aria-label={t("game.topBar.resume")}
        >
          ▶ <span className="control-label">{t("game.topBar.resume")}</span>
        </button>
      ) : (
        <button
          type="button"
          className="end-turn-button quiet pause-button"
          onClick={handlePause}
          disabled={noPausesLeft}
          title={
            noPausesLeft
              ? t("game.topBar.noPausesLeft")
              : t("game.topBar.pauseTitle", {
                  minutes: Math.round((pauseRules?.MAX_PAUSE_SECONDS ?? 300) / 60),
                  left: left ?? "—",
                })
          }
          aria-label={t("game.topBar.pause")}
        >
          ⏸ <span className="control-label">{t("game.topBar.pause")}</span>
        </button>
      )}
      {hostGone && isPlayerReady && (
        <span className="host-gone-notice" role="status">
          {claimable
            ? t("game.topBar.hostGone", { host: game.host.username })
            : t("game.topBar.hostGoneCountdown", { host: game.host.username, time: timeLeftLabel(secondsLeft) })}
        </span>
      )}
      {claimable && isPlayerReady ? (
        <button type="button" className="end-turn-button claim-host-button" onClick={handleTurnRound}>
          {t("game.topBar.turnRound")}
        </button>
      ) : (
        <button
          type="button"
          className={`end-turn-button${isPlayerReady ? " quiet" : ""}`}
          data-action="end-turn"
          onClick={isPlayerReady ? handleCancelReady : handleEndTurn}
          title={isPlayerReady ? t("game.topBar.cancelReadyTitle") : undefined}
        >
          {isPlayerReady ? t("game.topBar.cancelReady") : t("game.topBar.endTurn")}
        </button>
      )}
    </div>
  )
}

export default RoundHandler
